/**
 * Diagnostic: which of Word's insert paths does this host accept?
 *
 * Word for Mac refused the template's cover pages (a valid .docx) with a
 * bare GeneralException on 28 and 30 Sep, through every variant tried. Rather
 * than guess again, this tries each path in turn on the open document, takes
 * the inserted content out again, and reports what the host said.
 */
import { describeError } from "./errors";
import { withTrackingOff } from "./tracking";

export type ProbePayload = {
  original_docx_base64: string;
  front_docx_base64: string;
  front_docx_plain_base64: string;
  minimal_docx_base64: string;
  ooxml_paragraph: string;
  /** Lighter forms of the cover pages (see the server's slim_docx). */
  front_fallbacks?: { name: string; docx_base64: string }[];
  /** The lean cover as a flat OOXML package. */
  front_ooxml?: string | null;
  theme_numbering_ooxml?: string | null;
};

type Attempt = { name: string; ok: boolean; error?: string; ms: number };

const host = () => {
  try {
    const d = Office.context.diagnostics;
    return { host: String(d.host), platform: String(d.platform), version: d.version };
  } catch {
    return { host: "unknown", platform: "unknown", version: "unknown" };
  }
};

const apiSets = () => {
  const out: Record<string, boolean> = {};
  for (const v of ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "1.8", "1.9"]) {
    try {
      out[`WordApi ${v}`] = Office.context.requirements.isSetSupported("WordApi", v);
    } catch {
      out[`WordApi ${v}`] = false;
    }
  }
  for (const v of ["1.1", "1.2", "1.3"]) {
    try {
      out[`WordApiDesktop ${v}`] = Office.context.requirements.isSetSupported("WordApiDesktop", v);
    } catch {
      out[`WordApiDesktop ${v}`] = false;
    }
  }
  return out;
};

const timed = async (name: string, run: () => Promise<void>): Promise<Attempt> => {
  const started = performance.now();
  try {
    await run();
    return { name, ok: true, ms: Math.round(performance.now() - started) };
  } catch (error) {
    const debug = (error as { debugInfo?: unknown })?.debugInfo;
    return {
      name,
      ok: false,
      error: `${describeError(error)}${debug ? ` ${JSON.stringify(debug)}` : ""}`,
      ms: Math.round(performance.now() - started),
    };
  }
};

/** Insert, sync, then delete what was inserted. */
const fileInsert = (docx: string, where: "Start" | "End") => () =>
  Word.run(async (context) => {
    const range = context.document.body.insertFileFromBase64(docx, where);
    await context.sync();
    range.delete();
    await context.sync();
  });

const paragraphFileInsert = (docx: string) => () =>
  Word.run(async (context) => {
    const anchor = context.document.body.insertParagraph("", Word.InsertLocation.end);
    const range = anchor.insertFileFromBase64(docx, Word.InsertLocation.replace);
    await context.sync();
    range.delete();
    await context.sync();
  });

const ooxmlInsert = (pkg: string) => () =>
  Word.run(async (context) => {
    const range = context.document.body.insertOoxml(pkg, Word.InsertLocation.end);
    await context.sync();
    range.delete();
    await context.sync();
  });

/** A one-paragraph OOXML package built here, so a failed apply can probe without a server round trip. */
const paragraphPackage = (text: string): string =>
  `<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">` +
  `<pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml"><pkg:xmlData>` +
  `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" ` +
  `Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>` +
  `</pkg:xmlData></pkg:part>` +
  `<pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"><pkg:xmlData>` +
  `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body><w:p><w:r><w:t>${text}</w:t></w:r></w:p></w:body></w:document>` +
  `</pkg:xmlData></pkg:part></pkg:package>`;

/**
 * What a failed cover insert can find out on the spot: does this host take a
 * plain OOXML paragraph, the whole template file, and the extract at each
 * location? Runs the same attempts as the full probe minus the generated
 * minimal file (which needs the server). Never throws.
 */
export const diagnoseInserts = async (parts: {
  docx_base64: string;
  front_docx_base64?: string;
  front_ooxml?: string;
}): Promise<{ host: string; platform: string; version: string; attempts: Attempt[] }> => {
  const attempts: Attempt[] = [];
  try {
    attempts.push(
      await timed("OOXML paragraph, body.insertOoxml at End", ooxmlInsert(paragraphPackage("mytender insert probe")))
    );
    attempts.push(
      await timed("whole template file, body.insertFileFromBase64 at End", fileInsert(parts.docx_base64, "End"))
    );
    if (parts.front_docx_base64) {
      attempts.push(
        await timed("cover extract, body.insertFileFromBase64 at End", fileInsert(parts.front_docx_base64, "End"))
      );
      attempts.push(
        await timed("cover extract, body.insertFileFromBase64 at Start", fileInsert(parts.front_docx_base64, "Start"))
      );
    }
    if (parts.front_ooxml) {
      attempts.push(
        await timed("cover extract as OOXML package, body.insertOoxml at End", ooxmlInsert(parts.front_ooxml))
      );
    }
  } catch (error) {
    attempts.push({ name: "diagnostics", ok: false, error: describeError(error), ms: 0 });
  }
  return { ...host(), attempts };
};

export const probeInserts = async (p: ProbePayload) =>
  withTrackingOff(async () => {
    const attempts: Attempt[] = [];
    attempts.push(await timed("OOXML paragraph, body.insertOoxml at End", ooxmlInsert(p.ooxml_paragraph)));
    attempts.push(
      await timed("minimal generated .docx, body.insertFileFromBase64 at End", fileInsert(p.minimal_docx_base64, "End"))
    );
    attempts.push(
      await timed(
        "minimal generated .docx, body.insertFileFromBase64 at Start",
        fileInsert(p.minimal_docx_base64, "Start")
      )
    );
    attempts.push(
      await timed(
        "original template .docx, body.insertFileFromBase64 at End",
        fileInsert(p.original_docx_base64, "End")
      )
    );
    attempts.push(
      await timed("cover extract, body.insertFileFromBase64 at End", fileInsert(p.front_docx_base64, "End"))
    );
    attempts.push(
      await timed("cover extract, body.insertFileFromBase64 at Start", fileInsert(p.front_docx_base64, "Start"))
    );
    attempts.push(
      await timed(
        "cover extract, fresh paragraph.insertFileFromBase64 Replace",
        paragraphFileInsert(p.front_docx_base64)
      )
    );
    attempts.push(
      await timed("cover extract without section properties, body End", fileInsert(p.front_docx_plain_base64, "End"))
    );
    for (const f of p.front_fallbacks ?? []) {
      attempts.push(await timed(`cover extract, ${f.name}, body End`, fileInsert(f.docx_base64, "End")));
    }
    if (p.front_ooxml) {
      attempts.push(await timed("cover extract as OOXML package, body.insertOoxml at End", ooxmlInsert(p.front_ooxml)));
    }
    if (p.theme_numbering_ooxml)
      attempts.push(
        await timed("theme + numbering package, body.insertOoxml at End", ooxmlInsert(p.theme_numbering_ooxml))
      );
    return { ...host(), api_sets: apiSets(), attempts };
  });
